import axios from 'axios';

const api = axios.create({ baseURL: '/api', headers: { 'Content-Type': 'application/json' } });

api.interceptors.request.use(function(c) {
  var t = localStorage.getItem('okc_token');
  if (t) c.headers.Authorization = 'Bearer ' + t;
  return c;
});

api.interceptors.response.use(function(r) { return r; }, function(e) {
  if (e.response && e.response.status === 401) {
    localStorage.removeItem('okc_token');
    localStorage.removeItem('okc_user');
    window.location.href = '/login';
  }
  return Promise.reject(e);
});

export var authAPI = {
  completeReset: function(token, password) {
    return axios.post('/api/public/reset-password', { token: token, password: password });
  },
  login: function(email, password) { return api.post('/auth/login', { email: email, password: password }); },
  acceptInvite: function(token, password) { return api.post('/auth/accept-invite', { token: token, password: password }); },
  me: function() { return api.get('/auth/me'); },
  changePassword: function(cur, next) { return api.post('/auth/change-password', { current_password: cur, new_password: next }); },
  changeEmail: function(newEmail, password) { return api.post('/auth/change-email', { new_email: newEmail, password: password }); },
  get2FAStatus: function() { return api.get('/auth/2fa/status'); },
  setup2FA: function() { return api.post('/auth/2fa/setup'); },
  confirm2FA: function(code) { return api.post('/auth/2fa/confirm', { code: code }); },
  disable2FA: function(password) { return api.post('/auth/2fa/disable', { password: password }); },
  verify2FA: function(userId, code) { return api.post('/auth/2fa/verify-login', { user_id: userId, code: code }); },
};

export var formsAPI = {
  list: function(type) { return api.get('/admin/forms', { params: { type: type } }); },
  get: function(id) { return api.get('/admin/forms/' + id); },
  create: function(data) { return api.post('/admin/forms', data); },
  update: function(id, data) { return api.put('/admin/forms/' + id, data); },
  delete: function(id) { return api.delete('/admin/forms/' + id); },
  getPublic: function(id) { return axios.get('/api/public/forms/' + id); },
};

export var applicationsAPI = {
  list: function(formId, params) { return api.get('/admin/forms/' + formId + '/applications', { params: params }); },
  get: function(appId) { return api.get('/admin/applications/' + appId); },
  update: function(appId, data) { return api.put('/admin/applications/' + appId, data); },
  delete: function(appId) { return api.delete('/admin/applications/' + appId); },
  // Soft-discard: takes the application out of review, keeps every row.
  discard: function(appId, reason) { return api.post('/admin/applications/' + appId + '/discard', { reason: reason || '' }); },
  restore: function(appId) { return api.post('/admin/applications/' + appId + '/restore'); },
  export: function(formId, view) { return api.get('/admin/forms/' + formId + '/applications/export', { params: { view: view }, responseType: 'blob' }); },
  submit: function(formId, formData) {
    if (formData instanceof FormData) return axios.post('/api/public/forms/' + formId + '/apply', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    return axios.post('/api/public/forms/' + formId + '/apply', formData);
  },
};

export var draftsAPI = {
  checkEmail: function(formId, email) { return axios.post('/api/public/forms/' + formId + '/check-email', { email: email }); },
  sendOTP: function(formId, email, name) { return axios.post('/api/public/forms/' + formId + '/send-otp', { email: email, name: name }); },
  verifyOTP: function(formId, email, otp) { return axios.post('/api/public/forms/' + formId + '/verify-otp', { email: email, otp: otp }); },
  saveDraft: function(formId, draftToken, responses, name) { return axios.post('/api/public/forms/' + formId + '/save-draft', { draft_token: draftToken, responses: responses, name: name }); },
  loadDraft: function(formId, token) { return axios.get('/api/public/forms/' + formId + '/resume/' + token); },
  list: function(formId) { return api.get('/admin/forms/' + formId + '/drafts'); },
};

export var captchaAPI = { generate: function() { return axios.get('/api/public/captcha'); } };

export var criteriaAPI = {
  get: function(formId) { return api.get('/admin/forms/' + formId + '/criteria'); },
  save: function(formId, data) { return api.post('/admin/forms/' + formId + '/criteria', data); },
};

export var assignmentsAPI = {
  getConfig: function(formId) { return api.get('/admin/forms/' + formId + '/assignment-config'); },
  saveConfig: function(formId, data) { return api.post('/admin/forms/' + formId + '/assignment-config', data); },
  autoAssign: function(formId) { return api.post('/admin/forms/' + formId + '/auto-assign'); },
  manualAssign: function(appId, revId) { return api.post('/admin/assignments', { application_id: appId, reviewer_id: revId }); },
  remove: function(id) { return api.delete('/admin/assignments/' + id); },
  list: function(formId) { return api.get('/admin/forms/' + formId + '/assignments'); },
};

export var reviewersAPI = {
  list: function(role) { return api.get('/admin/reviewers', { params: { role: role } }); },
  invite: function(data) { return api.post('/admin/reviewers/invite', data); },
  toggle: function(id) { return api.patch('/admin/reviewers/' + id + '/toggle'); },
  delete: function(id) { return api.delete('/admin/reviewers/' + id); },
  setRole: function(id, data) { return api.patch('/admin/users/' + id + '/role', data); },
  resetPassword: function(id) { return api.post('/admin/users/' + id + '/reset-password'); },
  getAccess: function(id) { return api.get('/admin/users/' + id + '/viewer-access'); },
  setAccess: function(id, data) { return api.put('/admin/users/' + id + '/viewer-access', data); },
};

export var selectionAPI = {
  forms: function() { return api.get('/admin/selection/forms'); },
  applications: function(params) { return api.get('/admin/selection/applications', { params: params }); },
  application: function(id) { return api.get('/admin/selection/applications/' + id); },
};

export var facilitatorAPI = {
  overview: function() { return api.get('/facilitator/overview'); },
  remind: function(reviewerId, data) { return api.post('/facilitator/reviewers/' + reviewerId + '/remind', data); },
  flag: function(data) { return api.post('/facilitator/flags', data); },
  listReviewers: function(userId) { return api.get('/admin/users/' + userId + '/facilitator-reviewers'); },
  setReviewers: function(userId, data) { return api.put('/admin/users/' + userId + '/facilitator-reviewers', data); },
};

export var declarationsAPI = {
  mine: function() { return api.get('/me/declarations'); },
  log: function(formId) { return api.get('/admin/forms/' + formId + '/declaration-log'); },
};

export var dashboardsAPI = {
  list: function(formId) { return api.get('/admin/dashboards', { params: { form_id: formId } }); },
  save: function(data) { return api.post('/admin/dashboards', data); },
  remove: function(id) { return api.delete('/admin/dashboards/' + id); },
  data: function(id) { return api.get('/viewer/dashboards/' + id + '/data'); },
};

export var viewerAPI = {
  context: function() { return api.get('/viewer/context'); },
  dashboardData: function(id) { return api.get('/viewer/dashboards/' + id + '/data'); },
  applications: function(params) { return api.get('/viewer/applications', { params: params }); },
  application: function(id) { return api.get('/viewer/applications/' + id); },
};

export var reviewerAPI = {
  myAssignments: function() { return api.get('/reviewer/assignments'); },
  getAssignment: function(id) { return api.get('/reviewer/assignments/' + id); },
  submitReview: function(id, data) { return api.post('/reviewer/assignments/' + id + '/review', data); },
  saveDraft: function(id, data) { return api.post('/reviewer/assignments/' + id + '/draft', data); },
  pendingDeclarations: function() { return api.get('/reviewer/declarations/pending'); },
  respondToDeclaration: function(formId, data) {
    return api.post('/reviewer/declarations/' + formId + '/respond', data);
  },
};

export var decisionsAPI = {
  make: function(data) { return api.post('/admin/decisions', data); },
  list: function(formId) { return api.get('/admin/forms/' + formId + '/decisions'); },
  sendEmails: function(data) { return api.post('/admin/decisions/send-emails', data); },
};

export var emailTemplatesAPI = {
  list: function() { return api.get('/admin/email-templates'); },
  create: function(data) { return api.post('/admin/email-templates', data); },
  update: function(id, data) { return api.put('/admin/email-templates/' + id, data); },
  delete: function(id) { return api.delete('/admin/email-templates/' + id); },
};

export var smtpAPI = {
  get: function() { return api.get('/admin/smtp'); },
  save: function(data) { return api.post('/admin/smtp', data); },
  test: function() { return api.post('/admin/smtp/test'); },
};

export var registrationAPI = {
  submit: function(data) { return axios.post('/api/public/register', data); },
  validateToken: function(token) { return axios.get('/api/public/validate-token/' + token); },
  list: function(formId, params) { return api.get('/admin/forms/' + formId + '/registrations', { params: params }); },
};

export var shortURLsAPI = {
  list: function() { return api.get('/admin/short-urls'); },
  create: function(data) { return api.post('/admin/short-urls', data); },
  delete: function(id) { return api.delete('/admin/short-urls/' + id); },
};

export var priorityAPI = {
  recalculate: function(formId) { return api.post('/admin/forms/' + formId + '/recalculate-scores'); },
  status: function(formId) { return api.get('/admin/forms/' + formId + '/review-priority/status'); },
  release: function(formId, data) { return api.post('/admin/forms/' + formId + '/review-priority/release', data || {}); },
};

export var countriesAPI = {
  list: function() { return api.get('/admin/countries'); },
  create: function(data) { return api.post('/admin/countries', data); },
  update: function(id, data) { return api.put('/admin/countries/' + id, data); },
  remove: function(id) { return api.delete('/admin/countries/' + id); },
};

export var dashboardAPI = { stats: function() { return api.get('/admin/dashboard'); } };
export var auditAPI = { list: function(params) { return api.get('/admin/audit-log', { params: params }); } };

export var scholarAPI = {
  upload: function(formId, file) {
    var fd = new FormData(); fd.append('file', file);
    return api.post('/admin/forms/' + formId + '/scholars/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  list: function(formId) { return api.get('/admin/forms/' + formId + '/scholars'); },
  add: function(formId, data) { return api.post('/admin/forms/' + formId + '/scholars', data); },
  remove: function(formId, scholarId) { return api.delete('/admin/forms/' + formId + '/scholars/' + scholarId); },
  checkStatus: function(formId, email) { return axios.post('/api/public/forms/' + formId + '/check-scholar', { email: email }); },
};

export default api;
